import i18n from '@/utils/i18n';
import React from 'react';
import { AskChoiceArgs } from '@/experiences/sales-portal/domain/superintelligence/components/agent-frontend-tools';
import { Button, Chip, Typography } from '@usepepper/sprout-kit-ui';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { ToolCallPart } from '@/experiences/sales-portal/domain/superintelligence/blocs/utils/agent-message-parts';
import { useAui } from '@assistant-ui/store';
import { View } from 'react-native';

interface AgentAskChoiceProps {
  messageId: string;
  part: ToolCallPart;
}

const isAskChoiceArgs = (args: unknown): args is AskChoiceArgs =>
  typeof args === 'object' &&
  args !== null &&
  typeof (args as AskChoiceArgs).question === 'string' &&
  Array.isArray((args as AskChoiceArgs).options) &&
  (args as AskChoiceArgs).options.length > 0 &&
  (args as AskChoiceArgs).options.every((option) => typeof option === 'string');

export const AgentAskChoice: React.FC<AgentAskChoiceProps> = ({
  messageId,
  part,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();
  const aui = useAui();
  const chosen =
    typeof part.result === 'object' && part.result !== null
      ? (part.result as { choice?: unknown }).choice
      : undefined;

  if (!isAskChoiceArgs(part.args)) {
    const question = (part.args as { question?: unknown } | null)?.question;
    return (
      <View style={styles.choice}>
        {typeof question === 'string' ? (
          <Typography size="$md">{question}</Typography>
        ) : null}
        <Typography color={theme.sk.color.text.secondary} size="$sm">
          {i18n.t('sales_portal.superintelligence.choice_in_composer')}
        </Typography>
      </View>
    );
  }
  const { options, question } = part.args;

  return (
    <View style={styles.choice}>
      <Typography size="$md">{question}</Typography>
      {typeof chosen === 'string' ? (
        <Chip alignSelf="flex-start" selected>
          <Chip.Text>{chosen}</Chip.Text>
        </Chip>
      ) : (
        options.map((option) => (
          <Button
            key={option}
            onPress={() =>
              aui
                .thread()
                .message({ id: messageId })
                .part({ toolCallId: part.toolCallId })
                .addToolResult({ choice: option })
            }
            size="$sm"
            variant="secondary"
          >
            {option}
          </Button>
        ))
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  choice: {
    gap: theme.sk.spacing.spaceSmall,
  },
}));
